import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { NumberUtils, Tr } from '@utils';
import { useService } from '@hooks';
import {
	SimulationTask,
	SimulationTasksGroupedByCarListBand,
	Task,
	carListBandService,
	dealerConfigService,
	electrificationWizardService
} from '@services';
import { DataTable, RouterLinkAnchor } from '@components/common';
import { PortalPageTitle } from '@components/portal/common';
import { TasksListViewer } from '@components/portal/daily-tasks';
import { Box, Button, IconButton, Stack, Typography, TypographyProps } from '@mui/material';
import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';

const paddingX = 9;
type TableDataType = SimulationTasksGroupedByCarListBand | SimulationTask;
const CellFont: FC<TypographyProps> = (props) => <Typography fontWeight={400} {...props} />;

export const DailyTasksResult: FC = () => {
	const navigate = useNavigate();
	const [tasksGroupedByCarListBand, setTasksGroupedByCarListBand] = useState<SimulationTasksGroupedByCarListBand[]>([]);
	const { data: electrificationWizard } = useService(() => electrificationWizardService.getLatest());
	const { data: carListBands } = useService(() => carListBandService.getAllFromCache());
	const { data: dealerConfig } = useService(() => dealerConfigService.find(), []);

	const data: Task[] = [
		{
			id: '1',
			text: 'Replan itinerary for your cargo vans: XF747656, BV83Y22',
			app: 'RoutePlan',
			createdAt: new Date(),
			updatedAt: new Date(),
			link: 'http://link1.com',
			callToAction: 'Add App'
		},
		{
			id: '2',
			text: 'Identify the best sustainable alternatives for the drivers category “Directors”',
			app: 'EcoFleet',
			createdAt: new Date(),
			updatedAt: new Date(),
			link: 'http://link2.com',
			callToAction: 'Start'
		},
		{
			id: '3',
			text: 'Reach to the driver Domenico Scalera to complete the missing information',
			app: 'Voltaage',
			createdAt: new Date(),
			updatedAt: new Date(),
			link: 'http://link3.com',
			callToAction: 'Start'
		},
		{
			id: '4',
			text: 'Save up to 30.000€ by finding and planning the ideal solar panels plant for your location',
			app: 'EcoFleet',
			createdAt: new Date(),
			updatedAt: new Date(),
			link: 'http://link4.com',
			callToAction: 'Start'
		},
		{
			id: '5',
			text: 'Generate your personalized CO2 emissions report for the end of quarter meeting',
			app: 'Report',
			createdAt: new Date(),
			updatedAt: new Date(),
			link: 'http://link5.com',
			callToAction: 'Start'
		}
	];

	useEffect(() => {
		setTasksGroupedByCarListBand(
			electrificationWizard?.simulationTasks
				? SimulationTasksGroupedByCarListBand.fromTasks(electrificationWizard.simulationTasks, carListBands)
				: []
		);
	}, [electrificationWizard, carListBands]);

	const columns = useMemo<ColumnDef<TableDataType>[]>(
		() => [
			{
				accessorKey: 'conversionListTableName',
				header: () => <Tr.Portal path="conversion-list.car-band-list" />,
				enableSorting: false,
				cell: ({ row }) => {
					return (
						<Stack direction="row" alignItems="center" spacing={1}>
							{row.getCanExpand() ? (
								<IconButton onClick={row.getToggleExpandedHandler()} title="" size="small" sx={{ color: 'common.black' }}>
									<ArrowDropDownCircleIcon
										sx={{ transform: !row.getIsExpanded() ? undefined : 'rotate(180deg)' }}
										fontSize="small"
										color="inherit"
									/>
								</IconButton>
							) : (
								<Box sx={{ width: 32 }} />
							)}
							<CellFont>{row.original.conversionListTableName}</CellFont>
						</Stack>
					);
				}
			},
			{
				accessorKey: 'conversionListTableCurrentCar',
				header: () => <Tr.Portal path="conversion-list.current-cars" />,
				enableSorting: false,
				cell: ({ row }) => (
					<CellFont>
						{row.original.conversionListTableCurrentCar.length > 1 ? (
							<Tr.Portal path="conversion-list.various" />
						) : (
							row.original.conversionListTableCurrentCar[0]
						)}
					</CellFont>
				)
			},
			{
				accessorKey: 'conversionListTableEvSelection',
				header: () => <Tr.Portal path="conversion-list.final-selection" />,
				enableSorting: false,
				cell: ({ row }) => {
					return (
						<RouterLinkAnchor>
							<Typography>
								{row.original.conversionListTableEvSelection.length <= 0 && <Tr.Portal path="conversion-list.select" />}
								{row.original.conversionListTableEvSelection.length === 1 &&
									[
										row.original.conversionListTableEvSelection[0]?.brand,
										row.original.conversionListTableEvSelection[0]?.model
									]
										.filter(Boolean)
										.join(' ')}
								{row.original.conversionListTableEvSelection.length > 1 && <Tr.Portal path="conversion-list.various" />}
							</Typography>
						</RouterLinkAnchor>
					);
				}
			},
			...((dealerConfig?.isVehiclePriceVisible
				? [
						{
							accessorKey: 'conversionListTableLeasing',
							header: () => <Tr.Portal path="conversion-list.leasing" />,
							enableSorting: false,
							cell: ({ row }) => (
								<Typography>
									{NumberUtils.formatAsPrice(row.original.conversionListTableLeasing, { pureDefault: true })}
								</Typography>
							)
						}
				  ]
				: []) as ColumnDef<TableDataType>[])
		],
		[dealerConfig]
	);

	return (
		<Stack direction="column" spacing={4} sx={{ position: 'relative', height: 1.0, maxHeight: 1.0, paddingY: 5, overflow: 'hidden' }}>
			<PortalPageTitle
				paddingX={paddingX}
				title="Suggested tasks with Voltaage"
				subtitle="Daily ways to optimize your sustainable vehicles fleet based on your data"
			/>

			<Stack direction="column" spacing={4} paddingX={paddingX}>
				<Typography variant="h4" sx={{ fontWeight: 600 }}>
					Vehicle Renewals
				</Typography>
				<DataTable columns={columns} data={tasksGroupedByCarListBand} comparator={(d1, d2) => d1.id === d2.id} />
			</Stack>

			<Stack direction="column" sx={{ flexGrow: 1, maxHeight: 1.0, overflow: 'hidden' }}>
				<Stack direction="column" spacing={3} sx={{ flexGrow: 1, maxHeight: 1.0, overflow: 'hidden' }}>
					<Typography variant="h4" sx={{ fontWeight: 600 }} paddingX={paddingX}>
						Mobility Tasks
					</Typography>
					<TasksListViewer
						paddingX={paddingX}
						entities={data}
						// onSelectChange={}
						// onEdit={onEdit}
						// onDelete={(entity) => setDeletingEntities([entity])}
					/>
					<Box sx={{ spacing: 0, justifyContent: 'left', paddingX, marginTop: 0, paddingTop: 0 }}>
						<div>*AI-generated content may be incorrect</div>
					</Box>
				</Stack>
				<Stack direction="row" spacing={3} justifyContent="center" sx={{ paddingX }}>
					<Button
						variant="contained"
						color="secondary"
						size="small"
						// onClick={() => navigate('../start')}
					>
						Insert new Activity ({data.length})
					</Button>
					<Button variant="outlined" color="primary" size="small" onClick={() => navigate('../start')}>
						Generate More
					</Button>
					<Button variant="outlined" color="primary" size="small" onClick={() => navigate('../start')}>
						Edit
					</Button>
				</Stack>
			</Stack>
		</Stack>
	);
};
